import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import moment from 'moment';
import {
  Button, Col, Modal, Row, Spin, Collapse, Divider, notification,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { backendResponseCodes } from '../../../../utils/responseMessages';
import {
  get, isSuperAgentError, RequestWrapper, ResponseWrapper,
} from '../../../../dataSource/requests';
import './ServiceEstimateDetail.scss';
import SelectedLands from '../../serviceComponents/selectedLands/SelectedLands';
import { getUserType } from '../../../../utils/users';
import ServiceStatusComponent from '../../../sharedComponents/serviceStatusComponent/ServiceStatusComponent';
import UploadFiles from '../../serviceComponents/uploadFiles/UploadFiles';
import { downloadFile } from '../../../../utils/helpers';
import {
  CoopercitrusDetailsOutPut, EstimateRequestField,
} from '../../../../sharedTypes/EstimateTypes';
import { CommonDynamicField } from '../../../../sharedTypes/FieldTypes';
import AreaTalhao from '../../../sharedComponents/talhao/AreaTalhao';
import { formatTelephone } from '../../../../utils/format';
import { isRestructuredService, depthReferenceIdCodeList } from '../../../../utils/constants';
import EstimateDetailFields from './estimateComponents/EstimateDetailFields';

const ServiceEstimateDetail = (): JSX.Element => {
  const navigate = useNavigate();
  const routeMatch = useMatch('/service/estimates/:id/details');
  const [userType] = useState<string | null>(getUserType());
  const [isRestructured, setIsRestructured] = useState<boolean>(false);

  const [data, setData] = useState<CoopercitrusDetailsOutPut>({
    attachments: [],
    shapefiles: [],
    kml: [],
    kmz: [],
    landFields: [],
    serviceTypeDynamicFields: [],
    serviceSubtypeDynamicFields: [],
    budgetRequestNotes: [],
  });

  const [attachmentSizeCooperated, setAttachmentSizeCooperated] = useState<number>(0);

  const [loading, setLoading] = useState<boolean>(false);

  const [
    loadingDownloadAllServiceRequestFiles,
    setLoadingDownloadAllServiceRequestFiles,
  ] = useState<boolean>(false);

  const [
    loadingDownloadAllShapeFiles,
    setloadingDownloadAllShapeFiles,
  ] = useState<boolean>(false);

  const [
    loadingDownloadAllKMLFiles,
    setloadingDownloadAllKMLFiles,
  ] = useState<boolean>(false);

  const [
    loadingDownloadAllKMZFiles,
    setloadingDownloadAllKMZFiles,
  ] = useState<boolean>(false);

  const [statusesList] = useState([]);
  const [currentStatusIndex] = useState<number>(0);
  const [canceled] = useState<boolean>(false);
  const [visibleModalStatus, setVisibleModalStatus] = useState<boolean>(false);
  const [errorAnexo, setErrorAnexo] = useState<boolean>(false);
  const uploadFilesReference = useRef(null);

  const currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  useEffect(() => {
    const getSystemParameters = async (): Promise<void> => {
      setLoading(true);

      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: '/system/getAttachmentParamenters',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 8005):
          // "Attachment Paramenters Got Successfully", 8005
          setAttachmentSizeCooperated(
            response?.body.getAttachmentParamentersOutput.attachmentSizeCooperated,
          );
          break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao buscar os parametros de arquivos anexos, '
              + 'por favor tente novamente mais tarde.',
          });
          break;
      }

      setLoading(false);
    };
    getSystemParameters();
  }, []);

  const downloadAllFiles = async (): Promise<void> => {
    setErrorAnexo(false);
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const url = userType === 'COOPERATIVE'
      ? '/file/customer/downloadAllBudgetRequestFiles/'
      : '/file/downloadAllBudgetRequestFiles/';

    if (!routeMatch?.params.id) {
      notification.error({
        message: 'Erro',
        description: 'Desculpe, não foi possivel baixar os arquivos, por favor tente novamente mais tarde.',
      });
      return;
    }

    const requestReference: RequestWrapper = get({
      url: `${url}${routeMatch.params.id}`,

      frontConfigs: {
        navigateFunction: navigate,
      },

      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },

      responseType: 'blob',
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.statusCode === 200):
        downloadFile(response?.body, `anexos-cooperado-${routeMatch.params.id}.zip`);
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        setErrorAnexo(true);
        break;

      case (responseError?.body?.code === -5008):
        // "Service request not found", -5008
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .ServiceRequestNotFound.description.pt_br,
        });
        setErrorAnexo(true);
        break;

      case (responseError?.body?.code === -9006):
        // "Service request files not found", -9006
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.File
            .ServiceRequestFilesNotFound.description.pt_br,
        });
        setErrorAnexo(true);
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        // 'Ocorreu um erro ao baixar os Anexos, tente novamente'
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao baixar os anexos, por favor tente '
            + 'novamente mais tarde.',
        });
        setErrorAnexo(true);
        break;
    }
  };

  const downloadAllServiceRequestFiles = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    setLoadingDownloadAllServiceRequestFiles(true);

    let url = '/file/downloadAllBudgetRequestFiles/';

    if (userType === 'COOPERATIVE') {
      url = '/file/customer/downloadAllBudgetRequestFiles/';
    }

    if (!routeMatch?.params.id) {
      notification.error({
        message: 'Erro',
        description: 'Desculpe, não foi possivel baixar os arquivos, por favor tente novamente mais tarde.',
      });
      return;
    }

    const requestReference: RequestWrapper = get({
      url: `${url}${routeMatch.params.id}`,

      frontConfigs: {
        navigateFunction: navigate,
      },

      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },

      responseType: 'blob',
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.statusCode === 200):
        downloadFile(response?.body, `anexos-${routeMatch.params.id}.zip`);
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5008):
        // "Service request not found", -5008
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .ServiceRequestNotFound.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -9006):
        // "Service request files not found", -9006
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.File
            .ServiceRequestFilesNotFound.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        // 'Ocorreu um erro ao baixar os Anexos, tente novamente'
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao baixar os anexos, por favor tente '
            + 'novamente mais tarde.',
        });
        break;
    }

    setLoadingDownloadAllServiceRequestFiles(false);
  };

  const downloadAllShapefiles = async (): Promise<void> => {
    setloadingDownloadAllShapeFiles(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const url = '/file/downloadAllBudgetRequestShapefiles/';

    if (!routeMatch?.params.id) {
      notification.error({
        message: 'Erro',
        description: 'Desculpe, não foi possivel baixar os arquivos, por favor tente novamente mais tarde.',
      });
      return;
    }

    const requestReference: RequestWrapper = get({
      url: `${url}${routeMatch.params.id}`,

      frontConfigs: {
        navigateFunction: navigate,
      },

      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },

      responseType: 'blob',
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.statusCode === 200):
        downloadFile(response?.body, `anexos-${routeMatch.params.id}.zip`);
        break;

      case (responseError?.body.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body.code === -5008):
        // "Service request not found", -5008
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .ServiceRequestNotFound.description.pt_br,
        });
        break;

      case (responseError?.body.code === -9009):
        // "Shapefile could not be generated", -9009
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.File
            .ShapefileCouldNotBeGenerated.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao baixar os shapefiles, por favor '
            + 'tente novamente mais tarde.',
        });
        break;
    }

    setloadingDownloadAllShapeFiles(false);
  };

  const downloadAllKML = async (): Promise<void> => {
    setloadingDownloadAllKMLFiles(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const url = '/file/downloadAllBudgetRequestKml/';

    if (!routeMatch?.params.id) {
      notification.error({
        message: 'Erro',
        description: 'Desculpe, não foi possivel baixar os arquivos, por favor tente novamente mais tarde.',
      });
      return;
    }

    const requestReference: RequestWrapper = get({
      url: `${url}${routeMatch.params.id}`,

      frontConfigs: {
        navigateFunction: navigate,
      },

      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },

      responseType: 'blob',
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.statusCode === 200):
        downloadFile(response?.body, `anexos-${routeMatch.params.id}.zip`);
        break;

      case (responseError?.body.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body.code === -5008):
        // "Service request not found", -5008
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .ServiceRequestNotFound.description.pt_br,
        });
        break;

      case (responseError?.body.code === -9012):
        // "Kml could not be generated", -9012
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.File
            .KmlCouldNotBeGenerated.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao baixar os arquivos KML, por favor '
            + 'tente novamente mais tarde.',
        });
        break;
    }

    setloadingDownloadAllKMLFiles(false);
  };

  const downloadAllKMZ = async (): Promise<void> => {
    setloadingDownloadAllKMZFiles(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;
    const url = '/file/downloadAllBudgetRequestKmz/';

    if (!routeMatch?.params.id) {
      notification.error({
        message: 'Erro',
        description: 'Desculpe, não foi possivel baixar os arquivos, por favor tente novamente mais tarde.',
      });
      return;
    }

    const requestReference: RequestWrapper = get({
      url: `${url}${routeMatch.params.id}`,

      frontConfigs: {
        navigateFunction: navigate,
      },

      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },

      responseType: 'blob',
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.statusCode === 200):
        downloadFile(response?.body, `anexos-${routeMatch.params.id}.zip`);
        break;

      case (responseError?.body.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body.code === -5008):
        // "Service request not found", -5008
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .ServiceRequestNotFound.description.pt_br,
        });
        break;

      case (responseError?.body.code === -9014):
        // "Kmz could not be generated", -9014
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.File
            .KmzCouldNotBeGenerated.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao baixar os arquivos KMZ, por favor '
            + 'tente novamente mais tarde.',
        });
        break;
    }

    setloadingDownloadAllKMZFiles(false);
  };

  const getField = (idCodeParameter: string): EstimateRequestField | undefined => {
    return data
      .budgetRequestFields
      .find(
        (field) => {
          return field.idCode === idCodeParameter;
        },
      );
  };

  const handleValue = (fieldValue) => {
    switch (true) {
      case fieldValue === true:
      case fieldValue === 'true':
        return 'Sim';

      case fieldValue === false:
      case fieldValue === 'false':
        return 'Não';

      case (fieldValue?._isAMomentObject):
        return fieldValue.format('DD/MM/YYYY');

      case (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(fieldValue)):
        return moment(fieldValue, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]').format('DD/MM/YYYY');

      default:
        return fieldValue;
    }
  };

  const printField = (idCodeParameter: string): JSX.Element | null => {
    const field = getField(idCodeParameter);

    if (field !== undefined) {
      let fieldName = field.name;
      if (field.name === 'Profundidade') {
        depthReferenceIdCodeList.forEach((f) => {
          if (f.idCode === idCodeParameter) {
            fieldName = f.value;
          }
        });
      }
      return (
        <Row className="param-row" key={field.idCode}>
          <Col span={24}>
            {field.name === 'Profundidade' ? <hr /> : <></>}
            <Row justify="space-between">
              <Col>{`${fieldName}:`}</Col>
              <Col><b>{handleValue(field.value)}</b></Col>
            </Row>
          </Col>
        </Row>
      );
    }

    return null;
  };

  const renderGeofertGridFields = (): JSX.Element => {
    return (
      <>
        {printField('GEOFERT-GRID-TAMANHO_GRID')}
        {printField('GEOFERT-GRID-V_DESEJADO')}
        {printField('GEOFERT-GRID-PRNT_CALCARIO')}
        {printField('GEOFERT-GRID-PRNT_OXIDO')}
        {printField('GEOFERT-GRID-QUANTAS_PROFUNDIDADES')}

        {printField('GEOFERT-GRID-PROFUNDIDADE_1')}

        {printField('GEOFERT-GRID-ROTINA_ALUMINIO_ENXOFRE_PROFUNDIDADE_1')}
        {printField('GEOFERT-GRID-PROPORCAO_PROFUNDIDADE_1_ROTINA_ALUMINIO_ENXOFRE')}
        {printField('GEOFERT-GRID-MICRO_PROFUNDIDADE_1')}
        {printField('GEOFERT-GRID-PROPORCAO_PROFUNDIDADE_1_MICRO')}
        {printField('GEOFERT-GRID-FISICA_PROFUNDIDADE_1')}
        {printField('GEOFERT-GRID-PROPORCAO_PRODUNDIDADE_1_FISICA')}

        {printField('GEOFERT-GRID-PROFUNDIDADE_2')}

        {printField('GEOFERT-GRID-ROTINA_ALUMINIO_ENXOFRE_PROFUNDIDADE_2')}
        {printField('GEOFERT-GRID-PROPORCAO_PROFUNDIDADE_2_ROTINA_ALUMINIO_ENXOFRE')}
        {printField('GEOFERT-GRID-MICRO_PROFUNDIDADE_2')}
        {printField('GEOFERT-GRID-PROPORCAO_PROFUNDIDADE_2_MICRO')}
        {printField('GEOFERT-GRID-FISICA_PROFUNDIDADE_2')}
        {printField('GEOFERT-GRID-PROPORCAO_PRODUNDIDADE_2_FISICA')}

        {printField('GEOFERT-GRID-PROFUNDIDADE_3')}

        {printField('GEOFERT-GRID-ROTINA_ALUMINIO_ENXOFRE_PROFUNDIDADE_3')}
        {printField('GEOFERT-GRID-PROPORCAO_PROFUNDIDADE_3_ROTINA_ALUMINIO_ENXOFRE')}
        {printField('GEOFERT-GRID-MICRO_PROFUNDIDADE_3')}
        {printField('GEOFERT-GRID-PROPORCAO_PROFUNDIDADE_3_MICRO')}
        {printField('GEOFERT-GRID-FISICA_PROFUNDIDADE_3')}
        {printField('GEOFERT-GRID-PROPORCAO_PRODUNDIDADE_3_FISICA')}

        {printField('GEOFERT-GRID-PROFUNDIDADE_4')}

        {printField('GEOFERT-GRID-ROTINA_ALUMINIO_ENXOFRE_PROFUNDIDADE_4')}
        {printField('GEOFERT-GRID-PROPORCAO_PROFUNDIDADE_4_ROTINA_ALUMINIO_ENXOFRE')}
        {printField('GEOFERT-GRID-MICRO_PROFUNDIDADE_4')}
        {printField('GEOFERT-GRID-PROPORCAO_PROFUNDIDADE_4_MICRO')}
        {printField('GEOFERT-GRID-FISICA_PROFUNDIDADE_4')}
        {printField('GEOFERT-GRID-PROPORCAO_PRODUNDIDADE_4_FISICA')}

        {printField('GEOFERT-GRID-PROFUNDIDADE_5')}

        {printField('GEOFERT-GRID-ROTINA_ALUMINIO_ENXOFRE_PROFUNDIDADE_5')}
        {printField('GEOFERT-GRID-PROPORCAO_PROFUNDIDADE_5_ROTINA_ALUMINIO_ENXOFRE')}
        {printField('GEOFERT-GRID-MICRO_PROFUNDIDADE_5')}
        {printField('GEOFERT-GRID-PROPORCAO_PROFUNDIDADE_5_MICRO')}
        {printField('GEOFERT-GRID-FISICA_PROFUNDIDADE_5')}
        {printField('GEOFERT-GRID-PROPORCAO_PRODUNDIDADE_5_FISICA')}
      </>
    );
  };

  const renderGeofertZonaDeManejoSateliteFields = (): JSX.Element => {
    return (
      <>
        {printField('GEOFERT-ZONA_MANEJO-SATELITE-V_DESEJADO')}
        {printField('GEOFERT-ZONA_MANEJO-SATELITE-PRNT_CALCARIO')}
        {printField('GEOFERT-ZONA_MANEJO-SATELITE-PRNT_OXIDO')}
        {printField('GEOFERT-ZONA_MANEJO-VARIEDADE')}
        {printField('GEOFERT-ZONA_MANEJO-ESTAGIO_CORTE')}
        {printField('GEOFERT-ZONA_MANEJO-DATA_ULTIMO_CORTE')}
        {printField('GEOFERT-ZONA_MANEJO-DATA_PENULTIMO_CORTE')}
        {printField('GEOFERT-ZONA_MANEJO-DATA_PLANTIO')}
        {printField('GEOFERT-ZONA_MANEJO-HOUVE_TOMBAMENTO')}
        {printField('GEOFERT-ZONA_MANEJO-DATA_TOMBAMENTO')}
        {printField('GEOFERT-ZONA_MANEJO-HOUVE_APLICACAO_MATURADOR')}
        {printField('GEOFERT-ZONA_MANEJO-DATA_APLICACAO_MATURADOR')}
        {printField('GEOFERT-ZONA_MANEJO-NOME_PRODUTO_UTILIZADO_APLICACAO_MATURADOR')}
      </>
    );
  };

  const renderGeofertZonaDeManejoCondEletFields = (): JSX.Element => {
    return (
      <>
        {printField('GEOFERT-ZONA_MANEJO-CONDUTIVIDADE_ELETRICA-V_DESEJADO')}
        {printField('GEOFERT-ZONA_MANEJO-CONDUTIVIDADE_ELETRICA-PRNT_CALCARIO')}
        {printField('GEOFERT-ZONA_MANEJO-CONDUTIVIDADE_ELETRICA-PRNT_OXIDO')}
      </>
    );
  };

  const renderGeofertZDMVantFields = (): JSX.Element => {
    return (
      <>
        {printField('GEOFERT-ZONA_MANEJO-VANT-V_DESEJADO')}
        {printField('GEOFERT-ZONA_MANEJO-VANT-PRNT_CALCARIO')}
        {printField('GEOFERT-ZONA_MANEJO-VANT-PRNT_OXIDO')}
        {printField('GEOFERT-ZONA_MANEJO-VARIEDADE')}
        {printField('GEOFERT-ZONA_MANEJO-ESTAGIO_CORTE')}
        {printField('GEOFERT-ZONA_MANEJO-DATA_ULTIMO_CORTE')}
        {printField('GEOFERT-ZONA_MANEJO-DATA_PENULTIMO_CORTE')}
        {printField('GEOFERT-ZONA_MANEJO-DATA_PLANTIO')}
        {printField('GEOFERT-ZONA_MANEJO-HOUVE_TOMBAMENTO')}
        {printField('GEOFERT-ZONA_MANEJO-DATA_TOMBAMENTO')}
        {printField('GEOFERT-ZONA_MANEJO-HOUVE_APLICACAO_MATURADOR')}
        {printField('GEOFERT-ZONA_MANEJO-DATA_APLICACAO_MATURADOR')}
        {printField('GEOFERT-ZONA_MANEJO-NOME_PRODUTO_UTILIZADO_APLICACAO_MATURADOR')}
      </>
    );
  };

  const renderRestructuredServiceFields = (): JSX.Element | null => {
    switch (true) {
      case (data?.serviceName?.toUpperCase() === 'GEOFERT' && data?.serviceTypeName?.toUpperCase() === 'GRID'):
        return renderGeofertGridFields();

      case (data?.serviceName?.toUpperCase() === 'GEOFERT' && data?.serviceTypeName?.toUpperCase() === 'ZONA DE MANEJO' && data?.serviceSubtypeName?.toUpperCase() === 'SATÉLITE'):
        return renderGeofertZonaDeManejoSateliteFields();

      case (data?.serviceName?.toUpperCase() === 'GEOFERT' && data?.serviceTypeName?.toUpperCase() === 'ZONA DE MANEJO' && data?.serviceSubtypeName?.toUpperCase() === 'CONDUTIVIDADE ELÉTRICA'):
        return renderGeofertZonaDeManejoCondEletFields();

      case (data?.serviceName?.toUpperCase() === 'GEOFERT' && data?.serviceTypeName?.toUpperCase() === 'ZONA DE MANEJO' && data?.serviceSubtypeName?.toUpperCase() === 'VANT'):
        return renderGeofertZDMVantFields();

      default:
        return null;
    }
  };

  const renderDynamicFieldSummary = (
    field: CommonDynamicField, key: string,
  ): JSX.Element | null => {
    if (field.value === '01/01/1900') {
      return null;
    }

    if (field.value === '' || field.value == null) {
      return null;
    }

    return (
      <Row className="param-row" key={key}>
        <Col span={24}>
          <Row justify="space-between">
            <Col>{`${field.name}:`}</Col>
            <Col><b>{field.value}</b></Col>
          </Row>
        </Col>
      </Row>
    );
  };

  const renderGridFields = (
    field: CommonDynamicField,
    index: number,
    list: Array<CommonDynamicField>,
  ): JSX.Element | null => {
    if (field.name === 'Proporção') {
      return null;
    }

    if (index === 0) {
      return (
        <div key="subtype-0">
          <Row className="param-row">
            <Col span={24}>
              <Row justify="space-between">
                <Col>Tamanho do grid:</Col>
                <Col><b>{`${field.value} hectares`}</b></Col>
              </Row>
            </Col>
          </Row>
          <Row className="param-row">Amostragem da camada 0-20cm:</Row>
        </div>
      );
    }
    return (
      <div key={`subtype-${index}`}>
        {
          field.name === 'Rotina, Alumínio e Enxofre'
          && index !== 1
          && (
            <Row className="param-row">Amostragem da camada 20-40cm:</Row>
          )
        }
        {
          field.value === 'Sim'
            ? (
              <div>
                <Row className="section-row">
                  <Col xs={{ offset: 0, span: 24 }} lg={{ offset: 1, span: 23 }}>
                    <Row justify="space-between">
                      <Col>{field.name}</Col>
                      <Col><b>{`Proporção ${list?.[index + 1]?.value}`}</b></Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            )
            : (
              <div>
                <Row className="section-row">
                  <Col className="title" xs={{ offset: 0, span: 24 }} lg={{ offset: 1, span: 23 }}>
                    <Row justify="space-between">
                      <Col>{field.name}</Col>
                      <Col><b>Não solicitada</b></Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            )
        }
      </div>
    );
  };

  const conditionallyRenderGridAndSubtypeFields = (): (JSX.Element | null)[] | null => {
    if (!isRestructured) {
      if (data.serviceTypeName !== 'Grid') {
        return data.serviceSubtypeDynamicFields.map(
          (value, index) => renderDynamicFieldSummary(value, `subtype-${index}`),
        );
      }

      return data.serviceSubtypeDynamicFields.map(renderGridFields);
    }

    return null;
  };

  const loadData = async (id: string): Promise<void> => {
    setLoading(true);

    let url = '/budget/customer/findBudgetRequestDetails';

    if (userType === 'COOPERCITRUS') {
      url = '/budget/coopercitrus/findBudgetRequestDetails';
    }

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: `${url}/${+id}`,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (response?.body?.code === 13007): {
        // "Budget request found successfully", 13007
        setData(response?.body.details);
        const serviceFullName = `${response?.body.details.serviceName} ${(response?.body?.details?.serviceTypeName) ? response?.body.details?.serviceTypeName : ''} ${(response?.body.details?.serviceSubtypeName) ? response?.body.details?.serviceSubtypeName : ''}`;

        setIsRestructured(isRestructuredService(serviceFullName));
        setLoading(false);
      } break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .UserNotFound.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -14009):
        // "Budget request not found", -14009
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Budget
            .BudgetRequestNotFound.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        // 'Ocorreu um erro ao buscar os detalhes, tente novamente'
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar os detalhes, '
            + 'por favor tente novamente mais tarde',
        });
        break;
    }

    setLoading(false);
  };

  const uploadAtFiles = async (): Promise<void> => {
    const uploadError = !uploadFilesReference?.current?.validateFiles();

    setLoading(true);

    if (!uploadError) {
      try {
        await uploadFilesReference?.current?.handleSubmitFiles();
      } catch (error: unknown) {
        setLoading(false);
        return;
      }
    }

    if (!routeMatch?.params?.id) {
      notification.error({
        message: 'Erro',
        description: 'Desculpe, houve um erro ao fazer o upload dos arquivos, '
          + 'por favor tente novamente mais tarde.',
      });
      return;
    }

    loadData(routeMatch.params.id);
    setLoading(false);
  };

  useEffect(() => {
    if (!routeMatch?.params?.id) {
      notification.error({
        message: 'Erro',
        description: 'Desculpe, houve um erro ao buscar os detalhes, '
          + 'por favor tente novamente mais tarde.',
      });
      return;
    }

    loadData(routeMatch.params.id);

    return () => {
      setLoading(false);
    };
  }, []);

  return (
    <div className="ServiceEstimateDetail">
      <Spin
        tip="Carregando..."
        spinning={loading}
      >
        {
          userType === 'COOPERCITRUS'
            ? (
              <Row gutter={24}>
                <Col xs={{ span: 24 }} lg={{ span: 11 }}>
                  <span className="service-title">Detalhes da Solicitação</span>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 13 }}>
                  <div className="buttons-container">
                    <Row gutter={[24, 24]}>
                      <Col xs={{ span: 24 }} md={{ span: 8 }}>
                        <Button
                          className="btn-previous"
                          onClick={
                            (): void => {
                              navigate('/service/estimates');
                            }
                          }
                        >
                          Voltar
                        </Button>
                      </Col>

                      <Col xs={{ span: 24 }} md={{ span: 8 }}>
                        <Button
                          className="btn-refresh"
                          type="primary"
                          onClick={
                            (): void => {
                              navigate(`/estimate/status/${routeMatch?.params.id}`);
                            }
                          }
                        >
                          Ver status
                        </Button>
                      </Col>

                      <Col xs={{ span: 24 }} md={{ span: 8 }}>
                        <Button
                          href={`/service/estimates/${routeMatch?.params.id}/edit`}
                          className="btn-edit"
                          type="primary"
                          onClick={
                            (eventt): void => {
                              eventt.preventDefault();
                              navigate(`/service/estimates/${routeMatch?.params.id}/edit`);
                            }
                          }
                        >
                          Editar
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            )
            : (
              <Row gutter={24}>
                <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                  <span className="service-title">Detalhes</span>
                </Col>
                <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                  <div className="buttons-container">
                    <Button
                      href="/service/estimates"
                      className="btn-previous-cooperado"
                      onClick={
                        (eventt): void => {
                          eventt.preventDefault();
                          navigate('/service/estimates');
                        }
                      }
                    >
                      Voltar
                    </Button>
                  </div>
                </Col>
              </Row>
            )
        }

        <div className="inner-container">
          <Row gutter={24}>
            <Col xs={{ span: 24 }} lg={{ span: 11 }}>
              <div>
                {
                  data.serviceRequestId
                    ? (
                      <Row gutter={24}>
                        <Col xs={24} lg={24}>
                          <Button
                            className="item-margins"
                            type="link"
                            onClick={
                              (): void => {
                                navigate(`/service/detail/${data.serviceRequestId}`);
                              }
                            }
                          >
                            <p className="item-descripetion item-margins">
                              Serviço criado referente ao orçamento aprovado: {data.serviceRequestId}
                            </p>
                          </Button>
                        </Col>
                      </Row>
                    )
                    : null
                }

                <EstimateDetailFields
                  data={
                    {
                      id: routeMatch?.params.id,
                      budgetExpiredDate: data.budgetExpiredDate ? moment(data.budgetExpiredDate).utc().format('DD/MM/YYYY [às] HH:mm') : '-',
                      unitValue: (data.unitaryValue ? currencyFormatter.format(data.unitaryValue) : 'R$ 0,00'),
                      totalValue: (data.amount ? currencyFormatter.format(data.amount) : 'R$ 0,00'),
                      paymentCondition: (data.paymentTerms ? data.paymentTerms : '-'),
                      paymentForm: (data.formOfPayment ? data.formOfPayment : '-'),
                      estimateObs: (data.budgetNotes ? data.budgetNotes : '-'),
                      status: data.status,
                      approveOrRecuseDate: (data.approveOrRecuseDate ? moment(data.approveOrRecuseDate).format('DD/MM/YYYY [às] HH:mm') : '-'),
                      responsableBudget: data.responsableBudget,
                      reasonRefuses: data.reasonRefuses,
                      descriptionRefuse: data.descriptionRefuse,
                    }
                  }
                  loadData={loadData}
                />

                <Row gutter={24}>
                  <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                    <p className="item-title">ID da solicitação</p>
                    <p className="item-descripetion">{routeMatch?.params.id}</p>
                  </Col>
                  <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                    <p className="item-title">Status</p>
                    <p className="item-descripetion">{data.status}</p>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                    <p className="item-title">Data/hora da solicitação</p>
                    <p className="item-descripetion">
                      {
                        (data.solicitationDateTime
                          ? moment(data.solicitationDateTime).format('DD/MM/YYYY [às] HH:mm')
                          : '')
                      }
                    </p>
                  </Col>
                  <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                    <p className="item-title">Serviço/tipo de serviço</p>
                    <p className="item-descripetion">
                      {data.serviceName}
                      {data?.serviceTypeName && `: ${data.serviceTypeName}`}
                      {data?.serviceSubtypeName && ` - ${data.serviceSubtypeName}`}
                    </p>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                    <p className="item-title">Filial</p>
                    <p className="item-descripetion">
                      {data.branchCoopercitrus ? data.branchCoopercitrus.name : '-'}
                    </p>
                  </Col>
                  <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                    <p className="item-title">CTC responsável</p>
                    <p className="item-descripetion">
                      {data.ctc ? data.ctc : '-'}
                    </p>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                    <p className="item-title">Usuário que solicitou</p>
                    <p className="item-descripetion">{data.userNameThatRequestedTheService}</p>
                  </Col>
                  {
                    userType === 'COOPERCITRUS'
                    && (
                      <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                        <p className="item-title">Usuário responsável</p>
                        <p className="item-descripetion">{data.responsableUserName || '(Nenhum)'}</p>
                      </Col>
                    )
                  }
                  {
                    data.schedulingDate
                    && (
                      <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                        <p className="item-title">Data do agendamento</p>
                        <p className="item-descripetion">
                          {data.status === 'Agendado' ? moment(data.schedulingDate).format('DD/MM/YYYY')
                            : moment(data.schedulingDate).format('DD/MM/YYYY [às] HH:mm')}
                        </p>
                      </Col>
                    )
                  }
                </Row>
                <Row gutter={24}>
                  <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                    <p className="item-title">Telefone para contato</p>
                    <p className="item-descripetion">
                      {
                        !data.telephone
                          ? ''
                          : formatTelephone(data.telephone)
                      }
                    </p>
                  </Col>
                </Row>
                {
                  data.reasonForCancellation
                  && (
                    <Row gutter={24}>
                      <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                        <p className="item-title">Motivo do cancelamento</p>
                        <p className="item-descripetion">
                          {data.reasonForCancellation}
                        </p>
                      </Col>
                    </Row>
                  )
                }

                <Row className="param-row">
                  <Col>
                    <p className="item-title">Informações adicionais</p>
                    {`Matrícula: ${data.matriculationNumber}`}
                  </Col>
                </Row>

                <Row className="param-row">
                  <Col>
                    {`Cooperado: ${data.landOwnerName}`}
                  </Col>
                </Row>

                <Row className="param-row">
                  <Col>
                    {`Propriedade: ${data.landName}`}
                  </Col>
                </Row>

                {
                  data.serviceSubtypeName
                  && (
                    <Row className="param-row">
                      <Col span={24}>
                        <Row justify="space-between">
                          <Col>Tipo:</Col>
                          <Col><b>{data.serviceSubtypeName}</b></Col>
                        </Row>
                      </Col>
                    </Row>
                  )
                }
                {
                  isRestructured
                    && data?.budgetRequestFields?.length > 0
                    ? renderRestructuredServiceFields()
                    : null
                }

                {
                  !isRestructured
                    ? data.serviceTypeDynamicFields.map(
                      (value, index) => renderDynamicFieldSummary(value, `type-${index}`),
                    )
                    : null
                }
                {conditionallyRenderGridAndSubtypeFields()}

                <Row className="param-row">
                  <Col>
                    {
                      data.budgetRequestNotes !== []
                        && data.budgetRequestNotes != null
                        && data.budgetRequestNotes.filter((v) => v.type === 'COOPERATIVE').length
                        ? (
                          <div>
                            <p className="item-title">Observação:</p>
                            <Col>
                              {
                                data.budgetRequestNotes.map((v) => {
                                  if (v.type === 'COOPERATIVE') {
                                    return (
                                      <Row gutter={24} key={v.id} className="inline-field">
                                        <Col span={24}>
                                          <Col className="observacaoCol">{v.notes}</Col>
                                          <Divider type="horizontal" />
                                        </Col>
                                      </Row>
                                    );
                                  }
                                  return (
                                    <div></div>
                                  );
                                })
                              }
                            </Col>
                          </div>
                        ) : (
                          <div></div>
                        )
                    }
                  </Col>
                </Row>

                <Row>
                  <Col>
                    {data.budgetRequestNotes !== []
                      && data.budgetRequestNotes != null
                      && data.budgetRequestNotes.filter((v) => v.type === 'COOPERCITRUS').length
                      ? (
                        <div>
                          <p className="item-title">Observações da Coopercitrus:</p>
                          <Col>
                            {
                              data.budgetRequestNotes.map((v) => {
                                if (v.type === 'COOPERCITRUS') {
                                  return (
                                    <Row gutter={24} key={v.id} className="inline-field">
                                      <Col span={24}>
                                        <Col className="observacaoCol">{v.notes}</Col>
                                        <Divider type="horizontal" />
                                      </Col>
                                    </Row>
                                  );
                                }
                                return (
                                  <div></div>
                                );
                              })
                            }
                          </Col>
                        </div>
                      ) : (
                        <div></div>
                      )}
                  </Col>
                </Row>
                <Divider type="horizontal" />
              </div>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 12, offset: 1 }} style={{ paddingTop: 15 }}>
              <SelectedLands
                talhoesSelecionados={
                  data.landFields.map((v) => ({ ...v, culture: { description: v.culture } }))
                }
              />
              <AreaTalhao talhoesSelecionados={
                data.landFields.map((v) => ({ ...v, culture: { description: v.culture } }))
              }
              />

              <div className="attachments-container">
                <Row className="param-row">
                  <Col span={24}>
                    <Collapse defaultActiveKey={['0']}>
                      <Collapse.Panel header="Anexos do Cooperado" key="1">
                        <Row className="param-row">
                          <Col span={24}>
                            <Row
                              justify="end"
                              className="field-title-text"
                              style={{ marginBottom: 8 }}
                              gutter={[24, 24]}
                            >
                              <span
                                onClick={downloadAllFiles}
                                className="download-attachments"
                                role="button"
                                tabIndex={0}
                                onKeyDown={
                                  (eventt): void => {
                                    if (eventt.key === 'Enter') {
                                      downloadAllFiles();
                                    }
                                  }
                                }
                              >
                                Baixar todos os anexos
                              </span>
                            </Row>
                            <Row
                              justify="space-between"
                              style={{ marginBottom: 8 }}
                              gutter={[24, 24]}
                            >
                              <Col>
                                {
                                  data.attachments.length
                                    ? (
                                      <div></div>
                                    ) : (
                                      <div className="attachments-items-container">
                                        Nenhum anexo adicionado
                                      </div>
                                    )
                                }
                                <div className="attachments-items-container">
                                  <UploadFiles
                                    ref={uploadFilesReference}
                                    id={routeMatch?.params.id}
                                    origin="details"
                                    requestType="ORCAMENTO"
                                    typeUser="BUDGET_REQUEST_CUSTOMER_FILE"
                                    fileSize={attachmentSizeCooperated}
                                    action="edit"
                                    initialFiles={
                                      data.attachments.map((item) => ({ ...item, uid: item.uid, status: 'done' }))
                                    }
                                  />
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <Button
                                onClick={uploadAtFiles}
                              >
                                Confirmar
                              </Button>
                            </Row>
                          </Col>
                        </Row>
                      </Collapse.Panel>
                    </Collapse>
                    <Collapse defaultActiveKey={['0']}>
                      <Collapse.Panel header="Anexos do Coopercitrus" key="1">
                        <Row
                          justify="end"
                          className="field-title-text"
                          style={{ marginBottom: 8 }}
                          gutter={[24, 24]}
                        >
                          {
                            !!data.attachments.length && (
                              !loadingDownloadAllServiceRequestFiles
                                ? (
                                  <span
                                    onClick={downloadAllServiceRequestFiles}
                                    className="download-attachments"
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={
                                      (eventt): void => {
                                        if (eventt.key === 'Enter') {
                                          downloadAllServiceRequestFiles();
                                        }
                                      }
                                    }
                                  >
                                    Baixar todos os anexos
                                  </span>
                                )
                                : (
                                  <span
                                    className="downloading-attachments"
                                  >
                                    <Spin indicator={<LoadingOutlined className="load-icon" spin />} />
                                    Realizando Download...
                                  </span>
                                )
                            )
                          }
                        </Row>
                        <Row
                          justify="space-between"
                          style={{ marginBottom: 8 }}
                          gutter={[24, 24]}
                        >
                          <Col>
                            {
                              data.attachments.length
                                ? (
                                  <div className="attachments-items-container">
                                    <UploadFiles
                                      id={data.id}
                                      action="list"
                                      requestType="ORCAMENTO"
                                      typeUser="BUDGET_REQUEST_COOPERCITRUS_FILE"
                                      initialFiles={
                                        data.attachments.map((item) => ({ ...item, uid: item.id, status: 'done' }))
                                      }
                                    />
                                  </div>
                                )
                                : (
                                  <div className="attachments-items-container">
                                    Nenhum anexo adicionado
                                  </div>
                                )
                            }
                          </Col>
                        </Row>
                      </Collapse.Panel>
                    </Collapse>
                  </Col>
                </Row>
              </div>
              {
                userType === 'COOPERCITRUS'
                && (
                  <div className="shapefiles-container">
                    <Row className="param-row">
                      <Col span={24}>
                        <Row
                          className="field-title-text"
                          justify="space-between"
                          gutter={[24, 24]}
                          style={{ marginBottom: 8 }}
                        >
                          <Col>Arquivos geográficos dos talhões</Col>
                        </Row>
                      </Col>
                    </Row>
                    <Row className="param-row">
                      <Col span={24}>
                        <Collapse defaultActiveKey={['0']}>
                          <Collapse.Panel header="Shapefiles" key="2">
                            <Row
                              justify="end"
                              className="field-title-text"
                              style={{ marginBottom: 8 }}
                              gutter={[24, 24]}
                            >
                              <Col>
                                {
                                  !!data.shapefiles.length && (
                                    !loadingDownloadAllShapeFiles
                                      ? (
                                        <span
                                          onClick={downloadAllShapefiles}
                                          className="download-shapefiles"
                                          role="button"
                                          tabIndex={0}
                                          onKeyDown={
                                            (eventt): void => {
                                              if (eventt.key === 'Enter') {
                                                downloadAllShapefiles();
                                              }
                                            }
                                          }
                                        >
                                          Baixar todos os shapefiles
                                        </span>
                                      )
                                      : (
                                        <span
                                          className="downloading-shapefiles"
                                        >
                                          <Spin
                                            indicator={
                                              <LoadingOutlined className="load-icon" spin />
                                            }
                                          />
                                          Realizando Download...
                                        </span>
                                      )
                                  )
                                }
                              </Col>
                            </Row>
                            <Row>
                              {
                                data.shapefiles.length
                                  ? (
                                    <div className="shapefiles-items-container">
                                      <UploadFiles
                                        id={data.id}
                                        action="list"
                                        requestType="ORCAMENTO"
                                        initialFiles={
                                          data.shapefiles.map((item) => ({
                                            ...item,
                                            uid: item.id,
                                            status: 'done',
                                            type: 'shapefile',
                                          }))
                                        }
                                      />
                                    </div>
                                  )
                                  : (
                                    <div className="shapefiles-items-container">
                                      Nenhum anexo adicionado
                                    </div>
                                  )
                              }
                            </Row>
                          </Collapse.Panel>
                        </Collapse>
                      </Col>
                    </Row>
                    <Row className="param-row">
                      <Col span={24}>
                        <Collapse defaultActiveKey={['0']}>
                          <Collapse.Panel header="KML" key="3">
                            <Row
                              justify="end"
                              className="field-title-text"
                              style={{ marginBottom: 8 }}
                              gutter={[24, 24]}
                            >
                              <Col>
                                {
                                  !!data.kml.length && (
                                    !loadingDownloadAllKMLFiles
                                      ? (
                                        <span
                                          onClick={downloadAllKML}
                                          className="download-shapefiles"
                                          role="button"
                                          tabIndex={0}
                                          onKeyDown={
                                            (eventt): void => {
                                              if (eventt.key === 'Enter') {
                                                downloadAllKML();
                                              }
                                            }
                                          }
                                        >
                                          Baixar todos os Arquivos KML
                                        </span>
                                      )
                                      : (
                                        <span
                                          className="downloading-shapefiles"
                                        >
                                          <Spin
                                            indicator={
                                              <LoadingOutlined className="load-icon" spin />
                                            }
                                          />
                                          Realizando Download...
                                        </span>
                                      )
                                  )
                                }
                              </Col>
                            </Row>
                            <Row>
                              {
                                data.kml.length
                                  ? (
                                    <div className="shapefiles-items-container">
                                      <UploadFiles
                                        id={data.id}
                                        action="list"
                                        requestType="ORCAMENTO"
                                        initialFiles={
                                          data.kml.map((item) => ({
                                            ...item,
                                            uid: item.id,
                                            status: 'done',
                                            type: 'kml',
                                          }))
                                        }
                                      />
                                    </div>
                                  )
                                  : (
                                    <div className="shapefiles-items-container">
                                      Nenhum anexo adicionado
                                    </div>
                                  )
                              }
                            </Row>
                          </Collapse.Panel>
                        </Collapse>
                      </Col>
                    </Row>
                    <Row className="param-row">
                      <Col span={24}>
                        <Collapse defaultActiveKey={['0']}>
                          <Collapse.Panel header="KMZ" key="4">
                            <Row
                              justify="end"
                              className="field-title-text"
                              style={{ marginBottom: 8 }}
                              gutter={[24, 24]}
                            >
                              <Col>
                                {
                                  !!data.kmz.length && (
                                    !loadingDownloadAllKMZFiles
                                      ? (
                                        <span
                                          onClick={downloadAllKMZ}
                                          className="download-shapefiles"
                                          role="button"
                                          tabIndex={0}
                                          onKeyDown={
                                            (eventt): void => {
                                              if (eventt.key === 'Enter') {
                                                downloadAllKMZ();
                                              }
                                            }
                                          }
                                        >
                                          Baixar todos os KMZ
                                        </span>
                                      )
                                      : (
                                        <span
                                          className="downloading-shapefiles"
                                        >
                                          <Spin
                                            indicator={
                                              <LoadingOutlined className="load-icon" spin />
                                            }
                                          />
                                          Realizando Download...
                                        </span>
                                      )
                                  )
                                }
                              </Col>
                            </Row>
                            <Row>
                              {
                                data.kmz.length
                                  ? (
                                    <div className="shapefiles-items-container">
                                      <UploadFiles
                                        id={data.id}
                                        action="list"
                                        requestType="ORCAMENTO"
                                        initialFiles={
                                          data.kmz.map((item) => ({
                                            ...item,
                                            uid: item.id,
                                            status: 'done',
                                            type: 'kmz',
                                          }))
                                        }
                                      />
                                    </div>
                                  )
                                  : (
                                    <div className="shapefiles-items-container">
                                      Nenhum anexo adicionado
                                    </div>
                                  )
                              }
                            </Row>
                          </Collapse.Panel>
                        </Collapse>
                      </Col>
                    </Row>
                  </div>
                )
              }
            </Col>
          </Row>
        </div>
        <Modal
          wrapClassName="ServiceEstimateDetail-status-modal"
          width={1000}
          title={<h3 style={{ margin: 0 }}>Status da solicitação</h3>}
          visible={visibleModalStatus}
          onCancel={
            (): void => {
              setVisibleModalStatus(false);
            }
          }
          footer={[]}
        >
          {
            visibleModalStatus
            && (
              <ServiceStatusComponent
                loading={false}
                statusesList={statusesList}
                currentStatusIndex={currentStatusIndex}
                canceled={canceled}
              />
            )
          }
        </Modal>
      </Spin>
    </div>
  );
};

ServiceEstimateDetail.propTypes = {};
ServiceEstimateDetail.defaultProps = {};

export default ServiceEstimateDetail;
